import { API_ENDPOINTS } from '../config/api';
import { apiFetch } from './api';
import { cachedRequest } from './requestCache';

/** CRM feature keys used on this website. Check is case-insensitive (stored uppercase). */
export const FEATURE_KEYS = {
  LIVE_TV: 'LIVE_TV',
  WEB_STORE: 'WEB_STORE',
};

/** Share one /api/features response across Strict Mode remounts + visibility polls. */
const FEATURES_LIST_TTL_MS = 60_000;
const FEATURES_FLAGS_TTL_MS = 60_000;

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'TRUE';
}

/**
 * GET /api/features/check returns `{ success: true, data: true }` when on.
 * `data: true` is the switch — anything else is off.
 */
export function readFeatureFlagEnabled(json) {
  if (json == null) return false;
  if (isTruthyFlag(json)) return true;
  if (typeof json !== 'object') return false;
  return json.data === true || isTruthyFlag(json.data);
}

/** Env secret for X-Env-Key. Picks `REACT_APP_FEATURE_KEY_dev` / `_prod` via `REACT_APP_ENV`. */
export function getFeatureFlagEnvKey() {
  const mode = String(process.env.REACT_APP_ENV || '').trim().toLowerCase();
  const devKey = String(process.env.REACT_APP_FEATURE_KEY_dev || '').trim();
  const prodKey = String(process.env.REACT_APP_FEATURE_KEY_prod || '').trim();
  const legacy = String(
    process.env.REACT_APP_FEATURE_FLAGS_ENV_KEY || process.env.REACT_APP_FEATURE_KEY || '',
  ).trim();

  if (mode === 'prod' || mode === 'production') return prodKey || legacy;
  if (mode === 'dev' || mode === 'development') return devKey || legacy;
  if (process.env.NODE_ENV === 'production') return prodKey || legacy;
  return devKey || legacy;
}

/** `dev` | `prod` for GET /features enrollment, or empty to use /features/check instead. */
export function getFeatureFlagEnrollmentEnv() {
  const explicit = String(process.env.REACT_APP_FEATURE_FLAGS_ENV || '').trim().toLowerCase();
  if (explicit === 'prod' || explicit === 'dev') return explicit;
  const key = getFeatureFlagEnvKey().toLowerCase();
  if (key.includes('ff_prod') || key.includes('_prod_')) return 'prod';
  if (key.includes('ff_dev') || key.includes('_dev_')) return 'dev';
  return '';
}

function logFeatureFlagError(message) {
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`[featureFlags] ${message}`);
  }
}

/**
 * On when CRM `enabled` is truthy (or omitted) AND enrollment for this env is true.
 * Missing / unknown flag → off.
 */
function isFlagOnForEnrollment(flag, env) {
  if (!flag || (env !== 'prod' && env !== 'dev')) return false;
  if (flag.enabled === false || flag.enabled === 0 || flag.enabled === 'false') return false;
  return flag.enrollment?.[env] === true;
}

/** Public check — no JWT. Missing / disabled / 401 / error → false (fail closed). */
export async function checkFeature(featureKey) {
  const envKey = getFeatureFlagEnvKey();
  if (!envKey || !featureKey) return false;
  try {
    const res = await apiFetch(API_ENDPOINTS.featureCheck, {
      skipAuth: true,
      cache: 'no-store',
      headers: {
        'X-Feature-Key': String(featureKey),
        'X-Env-Key': envKey,
      },
    });
    if (res.status === 401) {
      logFeatureFlagError('invalid or missing X-Env-Key');
      return false;
    }
    if (!res.ok) return false;
    const json = await res.json();
    return readFeatureFlagEnabled(json);
  } catch {
    return false;
  }
}

/** Public list — no JWT. Used for value / nickname / enrollment. */
export async function fetchAllFeatures({ force = false } = {}) {
  return cachedRequest(
    'api:features',
    async () => {
      try {
        const res = await apiFetch(API_ENDPOINTS.features, { skipAuth: true, cache: 'no-store' });
        if (!res.ok) return [];
        const json = await res.json();
        return Array.isArray(json?.data) ? json.data : [];
      } catch {
        return [];
      }
    },
    { ttlMs: FEATURES_LIST_TTL_MS, force },
  );
}

export async function checkFeatures(keys) {
  const list = Array.isArray(keys) ? keys : [];
  const entries = await Promise.all(list.map(async (key) => [key, await checkFeature(key)]));
  return Object.fromEntries(entries);
}

/**
 * Load known flags at startup / refresh.
 * Prefers GET /features + enrollment.dev/prod when env is known.
 * Keys missing from the list fall back to GET /features/check (so partial CRM catalogs still work).
 * Deduped at module level so Strict Mode / multi-provider remounts share one request.
 */
export async function loadKnownFeatureFlags(keys = Object.values(FEATURE_KEYS), { force = false } = {}) {
  const list = Array.isArray(keys) ? keys : [];
  const cacheKey = `api:feature-flags:${list.map((k) => String(k).toUpperCase()).sort().join(',')}`;

  return cachedRequest(
    cacheKey,
    async () => {
      const empty = Object.fromEntries(list.map((key) => [key, false]));
      const env = getFeatureFlagEnrollmentEnv();

      if (env) {
        const all = await fetchAllFeatures({ force });
        if (all.length) {
          const records = Object.fromEntries(
            all
              .filter((flag) => flag?.featureKey)
              .map((flag) => [String(flag.featureKey).toUpperCase(), flag]),
          );
          const flags = {};
          const missing = [];
          for (const key of list) {
            const record = records[String(key).toUpperCase()];
            if (record) {
              flags[key] = isFlagOnForEnrollment(record, env);
            } else {
              missing.push(key);
              flags[key] = false;
            }
          }
          if (missing.length) {
            const checked = await checkFeatures(missing);
            for (const key of missing) {
              flags[key] = Boolean(checked[key]);
            }
          }
          return { flags, records };
        }
      }

      const flags = { ...empty, ...(await checkFeatures(list)) };
      return { flags, records: {} };
    },
    { ttlMs: FEATURES_FLAGS_TTL_MS, force },
  );
}

export function emptyFeatureFlags() {
  return {
    [FEATURE_KEYS.LIVE_TV]: false,
    [FEATURE_KEYS.WEB_STORE]: false,
  };
}
